import React, { useId, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  buildHrSeries,
  computeHrCurvePaths,
  extractAvailableHrRoles,
  extractRoleWindows,
  extractRoleWindowsFromPerformedSequences,
  mergeHrRoleWindows,
  normalizeSubjectRoleKind,
  HR_CURVE_VIEW_WIDTH,
  HR_CURVE_VIEW_HEIGHT,
  HR_CURVE_PLOT_LEFT,
  HR_CURVE_PLOT_WIDTH,
  HR_CURVE_PLOT_TOP,
  HR_CURVE_PLOT_BOTTOM,
  HR_CURVE_MOBILE_VIEW_WIDTH,
  HR_CURVE_MOBILE_VIEW_HEIGHT,
  HR_CURVE_MOBILE_PLOT_LEFT,
  HR_CURVE_MOBILE_PLOT_WIDTH,
  HR_CURVE_MOBILE_PLOT_TOP,
  HR_CURVE_MOBILE_PLOT_BOTTOM,
  HR_CURVE_DESKTOP_VIEW_WIDTH,
  HR_CURVE_DESKTOP_VIEW_HEIGHT,
  HR_CURVE_DESKTOP_PLOT_LEFT,
  HR_CURVE_DESKTOP_PLOT_WIDTH,
  HR_CURVE_DESKTOP_PLOT_TOP,
  HR_CURVE_DESKTOP_PLOT_BOTTOM,
} from "../utils/hrPerformanceCurve";
import {
  getZoneChartBands,
  isDarkModeActive,
  HR_CURVE_STROKE,
} from "../features/trainingSummary/zoneUtils";
import { useBrand } from "../context/BrandContext";

export function HrPerformanceCurveChart({
  hrInfo = null,
  mhr = 180,
  title = null,
  className = "",
  dark = false,
  highlightRole = null,
  showRoleToggle = false,
  onHighlightRoleChange = null,
  selectedSequence = null,
  onClearSelectedSequence = null,
  localeCode = undefined,
  /** Extra roles known from exercise sequences (active/sparring). */
  knownRoles = null,
  /**
   * Sequences the viewer performed (with roleVariant + seq.tes_start/end).
   * Used to paint Active/Sparring bands when HR windows lack subject roles.
   */
  performedSequences = null,
}) {
  const { t, i18n } = useTranslation();
  const brandCtx = useBrand?.() || {};
  const isDark = Boolean(
    dark ||
    brandCtx.themeMode === "dark" ||
    (!dark && brandCtx.themeMode !== "light" && isDarkModeActive())
  );
  const gradientId = useId().replace(/:/g, "");
  const highlightGradientId = `${gradientId}-hl`;
  const glowFilterId = `${gradientId}-glow`;
  const resolvedLocale = localeCode || i18n.language;

  const [curveLayout, setCurveLayout] = useState(getHrCurveLayout);
  const isMobile = curveLayout === "mobile";
  const isDesktop = curveLayout === "desktop";

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mobileMq = window.matchMedia("(max-width: 640px)");
    const desktopMq = window.matchMedia("(min-width: 900px)");
    const sync = () => setCurveLayout(getHrCurveLayout());
    sync();
    if (mobileMq.addEventListener) {
      mobileMq.addEventListener("change", sync);
      desktopMq.addEventListener("change", sync);
      return () => {
        mobileMq.removeEventListener("change", sync);
        desktopMq.removeEventListener("change", sync);
      };
    }
    mobileMq.addListener(sync);
    desktopMq.addListener(sync);
    return () => {
      mobileMq.removeListener(sync);
      desktopMq.removeListener(sync);
    };
  }, []);

  const hrPointsData = useMemo(() => buildHrSeries(hrInfo), [hrInfo]);
  const roleWindows = useMemo(() => {
    const fromApi = extractRoleWindows(hrInfo, hrPointsData.domain);
    const fromPerformed = extractRoleWindowsFromPerformedSequences(
      performedSequences,
      hrPointsData.domain,
      hrInfo
    );
    // Prefer API windows that already carry a role; fill gaps from performed list.
    return mergeHrRoleWindows(fromApi, fromPerformed);
  }, [hrInfo, hrPointsData.domain, performedSequences]);

  const availableRoles = useMemo(() => {
    const set = extractAvailableHrRoles(hrInfo, roleWindows);
    if (Array.isArray(knownRoles)) {
      for (const r of knownRoles) {
        const kind = normalizeSubjectRoleKind(r);
        if (kind === "active" || kind === "sparring") set.add(kind);
      }
    } else if (knownRoles instanceof Set) {
      for (const r of knownRoles) {
        const kind = normalizeSubjectRoleKind(r);
        if (kind === "active" || kind === "sparring") set.add(kind);
      }
    }
    return set;
  }, [hrInfo, roleWindows, knownRoles]);

  // Show Both/Active/Sparring whenever the parent asks for it and the curve
  // has data. Buttons for missing roles stay clickable (no bands) so the
  // control is never hidden when the player expects it.
  const canToggleRole = !!(showRoleToggle && hrPointsData.points.length);

  const resolvedHighlight = normalizeSubjectRoleKind(highlightRole);
  const hasSequenceSelection = !!(
    selectedSequence?.tesId ||
    (selectedSequence?.sequenceOrder != null &&
      Number.isFinite(Number(selectedSequence.sequenceOrder)))
  );

  const selectedWindowRole = useMemo(() => {
    if (!hasSequenceSelection) return null;
    const match = roleWindows.find((w) => {
      const tesMatch =
        selectedSequence?.tesId != null &&
        w.tes_id &&
        String(w.tes_id) === String(selectedSequence.tesId);
      const orderMatch =
        selectedSequence?.sequenceOrder != null &&
        w.sequence_order != null &&
        Number(w.sequence_order) === Number(selectedSequence.sequenceOrder);
      return tesMatch || orderMatch;
    });
    return match?.role || normalizeSubjectRoleKind(selectedSequence?.role) || null;
  }, [hasSequenceSelection, roleWindows, selectedSequence]);

  const selectedWindowMatched = useMemo(() => {
    if (!hasSequenceSelection) return false;
    return roleWindows.some((w) => {
      const tesMatch =
        selectedSequence?.tesId != null &&
        w.tes_id &&
        String(w.tes_id) === String(selectedSequence.tesId);
      const orderMatch =
        selectedSequence?.sequenceOrder != null &&
        w.sequence_order != null &&
        Number(w.sequence_order) === Number(selectedSequence.sequenceOrder);
      return tesMatch || orderMatch;
    });
  }, [hasSequenceSelection, roleWindows, selectedSequence]);

  const chartHighlightRole = hasSequenceSelection
    ? selectedWindowRole || resolvedHighlight
    : canToggleRole
      ? resolvedHighlight
      : null;

  const effectiveSelectedSequence =
    hasSequenceSelection && selectedWindowMatched ? selectedSequence : null;

  const curveDims = useMemo(() => getHrCurveDimensions(curveLayout), [curveLayout]);

  const hrCurvePaths = useMemo(
    () =>
      computeHrCurvePaths(hrPointsData.points, mhr, {
        domain: hrPointsData.domain,
        roleWindows,
        highlightRole: chartHighlightRole,
        selectedSequence: effectiveSelectedSequence,
        tickCount: isMobile ? 3 : 5,
        localeCode: resolvedLocale,
        ...curveDims,
      }),
    [
      hrPointsData.points,
      hrPointsData.domain,
      mhr,
      roleWindows,
      chartHighlightRole,
      effectiveSelectedSequence,
      resolvedLocale,
      isMobile,
      curveDims,
    ]
  );

  const zoneBands = useMemo(() => getZoneChartBands(mhr, isDark), [mhr, isDark]);

  if (!hrPointsData.points.length) {
    return null;
  }

  const headerText =
    title ?? t("hr_performance_curve", "Heart Rate Performance Curve (BPM)");

  const ROLE_ACTIVE = "#34C759";
  const ROLE_SPARRING = "#FF9800";
  const fillForRole = (role, selected) =>
    role === "sparring"
      ? selected
        ? "rgba(255, 152, 0, 0.42)"
        : "rgba(255, 152, 0, 0.32)"
      : selected
        ? "rgba(52, 199, 89, 0.40)"
        : "rgba(52, 199, 89, 0.30)";
  const strokeForRole = (role) =>
    role === "sparring" ? ROLE_SPARRING : ROLE_ACTIVE;

  const bandRole = chartHighlightRole === "sparring" ? "sparring" : "active";
  // Peak marker: role color when filtering; red max-HR dot in Both mode.
  const highlightStroke = chartHighlightRole
    ? strokeForRole(bandRole)
    : HR_CURVE_STROKE;
  const showingRoleSegments =
    !!chartHighlightRole &&
    (hrCurvePaths.highlightBands.length > 0 ||
      hrCurvePaths.highlightLinePaths.length > 0);
  const dimBase = shouldDimBaseCurve(
    canToggleRole,
    chartHighlightRole,
    !!effectiveSelectedSequence,
    showingRoleSegments
  );

  const chartViewWidth = hrCurvePaths.viewWidth || curveDims.viewWidth;
  const chartViewHeight = hrCurvePaths.viewHeight || curveDims.viewHeight;
  const plotLeft = hrCurvePaths.plotLeft ?? curveDims.plotLeft;
  const plotWidth = hrCurvePaths.plotWidth ?? curveDims.plotWidth;
  const plotTop = hrCurvePaths.plotTop ?? curveDims.plotTop;
  const plotBottom = hrCurvePaths.plotBottom ?? curveDims.plotBottom;
  const plotHeight = hrCurvePaths.plotHeight ?? (plotBottom - plotTop);

  const yTicks = hrCurvePaths.yTicks || [
    { bpm: Math.round(mhr), y: plotTop },
    { bpm: Math.round(mhr * 0.8), y: plotTop + plotHeight * (1 / 3) },
    { bpm: Math.round(mhr * 0.6), y: plotTop + plotHeight * (2 / 3) },
    { bpm: Math.round(mhr * 0.4), y: plotBottom },
  ];

  const yMinBpm = mhr * 0.4;
  const yMaxBpm = mhr;
  const getBpmY = (bpm) => {
    const clamped = Math.max(yMinBpm, Math.min(yMaxBpm, bpm));
    return plotBottom - ((clamped - yMinBpm) / (yMaxBpm - yMinBpm)) * plotHeight;
  };

  const selectedOrderLabel =
    selectedSequence?.sequenceOrder != null
      ? Number(selectedSequence.sequenceOrder)
      : null;
  const selectedRoleLabel =
    bandRole === "sparring"
      ? t("sparring", "Sparring")
      : t("performance_role", "Performance");

  const handleRoleChange = (role) => {
    onClearSelectedSequence?.();
    onHighlightRoleChange?.(role);
  };

  return (
    <div
      className={`player-exercise-details__hr-curve-card${
        isDark ? " player-exercise-details__hr-curve-card--dark" : ""
      }${isMobile ? " player-exercise-details__hr-curve-card--mobile" : ""}${
        isDesktop ? " player-exercise-details__hr-curve-card--desktop" : ""
      }${
        effectiveSelectedSequence ? " player-exercise-details__hr-curve-card--seq-selected" : ""
      }${className ? ` ${className}` : ""}`}
    >
      <div className="player-exercise-details__hr-curve-header-row">
        <div
          className="player-exercise-details__hr-curve-header"
          style={{ color: isDark ? "#ffffff" : "#0f172a" }}
        >
          {headerText}
        </div>
        {canToggleRole ? (
          <div
            className="player-exercise-details__hr-role-toggle"
            role="group"
            aria-label={t("hr_role_highlight", "Highlight role on chart")}
          >
            <button
              type="button"
              className={`player-exercise-details__hr-role-btn${
                !chartHighlightRole
                  ? " player-exercise-details__hr-role-btn--both"
                  : ""
              }`}
              aria-pressed={!chartHighlightRole}
              onClick={() => handleRoleChange(null)}
            >
              {t("hr_role_both", "Both")}
            </button>
            <button
              type="button"
              className={`player-exercise-details__hr-role-btn${
                chartHighlightRole === "active"
                  ? " player-exercise-details__hr-role-btn--active"
                  : ""
              }${
                !availableRoles.has("active")
                  ? " player-exercise-details__hr-role-btn--empty"
                  : ""
              }`}
              aria-pressed={chartHighlightRole === "active"}
              title={
                !availableRoles.has("active")
                  ? t(
                      "hr_role_no_windows",
                      "No sequences with this role in the HR window"
                    )
                  : undefined
              }
              onClick={() => handleRoleChange("active")}
            >
              {t("performance_role", "Performance")}
            </button>
            <button
              type="button"
              className={`player-exercise-details__hr-role-btn${
                chartHighlightRole === "sparring"
                  ? " player-exercise-details__hr-role-btn--sparring"
                  : ""
              }${
                !availableRoles.has("sparring")
                  ? " player-exercise-details__hr-role-btn--empty"
                  : ""
              }`}
              aria-pressed={chartHighlightRole === "sparring"}
              title={
                !availableRoles.has("sparring")
                  ? t(
                      "hr_role_no_windows",
                      "No sequences with this role in the HR window"
                    )
                  : undefined
              }
              onClick={() => handleRoleChange("sparring")}
            >
              {t("sparring", "Sparring")}
            </button>
          </div>
        ) : null}
      </div>

      {hasSequenceSelection ? (
        <p className="player-exercise-details__hr-role-hint player-exercise-details__hr-role-hint--selected">
          {selectedOrderLabel != null
            ? t(
                "hr_sequence_selected_hint",
                "Highlighting sequence {{order}} — your role: {{role}}.",
                { order: selectedOrderLabel, role: selectedRoleLabel }
              )
            : t(
                "hr_sequence_selected_hint_role",
                "Highlighting selected sequence — your role: {{role}}.",
                { role: selectedRoleLabel }
              )}
          {onClearSelectedSequence ? (
            <>
              {" "}
              <button
                type="button"
                className="player-exercise-details__hr-clear-seq"
                onClick={() => onClearSelectedSequence()}
              >
                {t("hr_clear_sequence_selection", "Show all")}
              </button>
            </>
          ) : null}
        </p>
      ) : canToggleRole && resolvedHighlight ? (
        <p className="player-exercise-details__hr-role-hint">
          {resolvedHighlight === "sparring"
            ? t(
                "hr_role_highlight_sparring_hint",
                "Orange bands mark sequences where you were sparring."
              )
            : t(
                "hr_role_highlight_active_hint",
                "Green bands mark sequences where you were active."
              )}
        </p>
      ) : canToggleRole ? (
        <p className="player-exercise-details__hr-role-hint">
          {t(
            "hr_role_highlight_both_hint",
            "Showing full curve. Tap Active or Sparring to highlight those sequences."
          )}
        </p>
      ) : null}

      <div className="player-exercise-details__hr-curve-body player-exercise-details__hr-curve-body--with-xticks">
        <div className="player-exercise-details__hr-curve-chart">
          <svg
            viewBox={`0 0 ${chartViewWidth} ${chartViewHeight}`}
            className="player-exercise-details__hr-svg"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <filter id={glowFilterId} x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="0" stdDeviation="2.5" floodColor={highlightStroke} floodOpacity="0.9" />
                <feDropShadow dx="0" dy="1" stdDeviation="6" floodColor={highlightStroke} floodOpacity="0.55" />
                <feDropShadow dx="0" dy="3" stdDeviation="12" floodColor={highlightStroke} floodOpacity="0.30" />
              </filter>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={HR_CURVE_STROKE} stopOpacity={isDark ? 0.38 : 0.28} />
                <stop offset="60%" stopColor={HR_CURVE_STROKE} stopOpacity={isDark ? 0.12 : 0.08} />
                <stop offset="100%" stopColor={HR_CURVE_STROKE} stopOpacity={0.0} />
              </linearGradient>
              <linearGradient id={highlightGradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={highlightStroke} stopOpacity="0.45" />
                <stop offset="60%" stopColor={highlightStroke} stopOpacity="0.15" />
                <stop offset="100%" stopColor={highlightStroke} stopOpacity="0.0" />
              </linearGradient>
            </defs>

            {/* Distinguished Training Zone Bands (labels removed for clean curve display) */}
            {zoneBands.map((zb) => {
              const yTop = getBpmY(zb.y2);
              const yBottom = getBpmY(zb.y1);
              const bandH = Math.max(0, yBottom - yTop);
              return (
                <g key={`zone-band-${zb.zone}`}>
                  <rect
                    x={plotLeft}
                    y={yTop}
                    width={plotWidth}
                    height={bandH}
                    fill={zb.color}
                    fillOpacity={zb.fillOpacity}
                  />
                  <line
                    x1={plotLeft}
                    y1={yTop}
                    x2={plotLeft + plotWidth}
                    y2={yTop}
                    stroke={zb.borderColor || (isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)")}
                    strokeWidth={1}
                    strokeDasharray={zb.zone === 5 ? undefined : "3 3"}
                  />
                </g>
              );
            })}

            {/* Bottom plot baseline */}
            <line
              x1={plotLeft}
              y1={plotBottom}
              x2={plotLeft + plotWidth}
              y2={plotBottom}
              stroke={isDark ? "rgba(255,255,255,0.30)" : "#0f172a"}
              strokeWidth={1.2}
            />

            {/* Left Y-Axis Ticks & Grid Lines directly inside SVG - zero offset */}
            {yTicks.map((tick, idx) => (
              <g key={`ytick-${idx}`}>
                <line
                  x1={plotLeft}
                  y1={tick.y}
                  x2={plotLeft + plotWidth}
                  y2={tick.y}
                  stroke={isDark ? "rgba(255,255,255,0.14)" : "rgba(15,23,42,0.12)"}
                  strokeWidth={1}
                  strokeDasharray={idx === 0 || idx === yTicks.length - 1 ? undefined : "3 3"}
                />
                <text
                  x={plotLeft - 6}
                  y={tick.y}
                  textAnchor="end"
                  dominantBaseline="central"
                  fill={isDark ? "rgba(255,255,255,0.92)" : "#0f172a"}
                  fontSize="10.5"
                  fontWeight="700"
                  fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                  style={{
                    fill: isDark ? "rgba(255,255,255,0.92)" : "#0f172a",
                    userSelect: "none",
                  }}
                >
                  {tick.bpm}
                </text>
              </g>
            ))}

            {/* Role Highlight Bands */}
            {hrCurvePaths.highlightBands.map((band, idx) => (
              <rect
                key={`band-${idx}`}
                x={band.x}
                y={plotTop}
                width={band.width}
                height={plotHeight}
                fill={fillForRole(band.role, !!effectiveSelectedSequence)}
                rx="2"
                className={
                  effectiveSelectedSequence
                    ? "player-exercise-details__hr-seq-band player-exercise-details__hr-seq-band--selected"
                    : undefined
                }
              />
            ))}

            {/* Shaded Area under curve */}
            {hrCurvePaths.areaPath ? (
              <path
                d={hrCurvePaths.areaPath}
                fill={`url(#${gradientId})`}
                opacity={dimBase ? 0.35 : 1}
              />
            ) : null}

            {/* Main HR Curve line with iOS glow */}
            {hrCurvePaths.linePath ? (
              <path
                d={hrCurvePaths.linePath}
                fill="none"
                stroke={chartHighlightRole ? strokeForRole(bandRole) : HR_CURVE_STROKE}
                strokeWidth={isMobile ? "3.6" : "3.2"}
                strokeLinecap="round"
                strokeLinejoin="round"
                filter={`url(#${glowFilterId})`}
                opacity={dimBase ? 0.28 : 1}
                className="player-exercise-details__hr-line-path"
              />
            ) : null}

            {/* Highlighted Role Line Segments */}
            {hrCurvePaths.highlightLinePaths.map((entry, idx) => {
              const path = typeof entry === "string" ? entry : entry?.path;
              const role =
                typeof entry === "string"
                  ? bandRole
                  : entry?.role || bandRole;
              if (!path) return null;
              return (
                <path
                  key={`hl-${idx}`}
                  d={path}
                  fill="none"
                  stroke={strokeForRole(role)}
                  strokeWidth={effectiveSelectedSequence ? 4.2 : 3.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  filter={`url(#${glowFilterId})`}
                />
              );
            })}

            {/* Peak Concentric Pulsing Indicator matching iOS */}
            {hrCurvePaths.maxPoint && (
              <g key="peak-pulse-group">
                <circle
                  cx={hrCurvePaths.maxPoint.x}
                  cy={hrCurvePaths.maxPoint.y}
                  r={isMobile ? "14" : "13"}
                  fill={highlightStroke}
                  fillOpacity="0.22"
                >
                  <animate
                    attributeName="r"
                    values={isMobile ? "11;17;11" : "10;16;10"}
                    dur="2.4s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.65;0.12;0.65"
                    dur="2.4s"
                    repeatCount="indefinite"
                  />
                </circle>
                <circle
                  cx={hrCurvePaths.maxPoint.x}
                  cy={hrCurvePaths.maxPoint.y}
                  r={isMobile ? "10" : "9"}
                  fill="none"
                  stroke={highlightStroke}
                  strokeWidth="1.8"
                  className="pulse-ring"
                />
                <circle
                  cx={hrCurvePaths.maxPoint.x}
                  cy={hrCurvePaths.maxPoint.y}
                  r="6"
                  fill={highlightStroke}
                  filter={`url(#${glowFilterId})`}
                />
                <circle
                  cx={hrCurvePaths.maxPoint.x}
                  cy={hrCurvePaths.maxPoint.y}
                  r="4"
                  fill={highlightStroke}
                  stroke="#FFFFFF"
                  strokeWidth="2"
                />
              </g>
            )}

            {/* X-Axis Time Ticks */}
            {hrCurvePaths.xTicks.map((tick, idx) => (
              <g key={`xtick-${idx}`}>
                <line
                  x1={tick.x}
                  y1={plotBottom}
                  x2={tick.x}
                  y2={plotBottom + 5}
                  stroke={isDark ? "rgba(255,255,255,0.60)" : "#0f172a"}
                  strokeWidth="1.2"
                />
                <text
                  x={tick.x}
                  y={plotBottom + (isMobile ? 18 : 17)}
                  textAnchor={
                    idx === 0
                      ? "start"
                      : idx === hrCurvePaths.xTicks.length - 1
                        ? "end"
                        : "middle"
                  }
                  className="player-exercise-details__hr-xtick-label"
                  fill={isDark ? "rgba(255,255,255,0.92)" : "#0f172a"}
                  fontSize={isMobile ? "10.5" : "11"}
                  fontWeight="700"
                  fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                  style={{
                    fill: isDark ? "rgba(255,255,255,0.92)" : "#0f172a",
                    userSelect: "none",
                  }}
                >
                  {tick.label}
                </text>
              </g>
            ))}
          </svg>
        </div>
      </div>
    </div>
  );
}

function getHrCurveLayout() {
  if (typeof window === "undefined") return "desktop";
  const width = window.innerWidth;
  if (width <= 640) return "mobile";
  if (width >= 900) return "desktop";
  return "tablet";
}

function getHrCurveDimensions(layout) {
  if (layout === "mobile") {
    return {
      viewWidth: HR_CURVE_MOBILE_VIEW_WIDTH,
      viewHeight: HR_CURVE_MOBILE_VIEW_HEIGHT,
      plotLeft: HR_CURVE_MOBILE_PLOT_LEFT,
      plotWidth: HR_CURVE_MOBILE_PLOT_WIDTH,
      plotTop: HR_CURVE_MOBILE_PLOT_TOP,
      plotBottom: HR_CURVE_MOBILE_PLOT_BOTTOM,
    };
  }
  if (layout === "desktop") {
    return {
      viewWidth: HR_CURVE_DESKTOP_VIEW_WIDTH,
      viewHeight: HR_CURVE_DESKTOP_VIEW_HEIGHT,
      plotLeft: HR_CURVE_DESKTOP_PLOT_LEFT,
      plotWidth: HR_CURVE_DESKTOP_PLOT_WIDTH,
      plotTop: HR_CURVE_DESKTOP_PLOT_TOP,
      plotBottom: HR_CURVE_DESKTOP_PLOT_BOTTOM,
    };
  }
  return {
    viewWidth: HR_CURVE_VIEW_WIDTH,
    viewHeight: HR_CURVE_VIEW_HEIGHT,
    plotLeft: HR_CURVE_PLOT_LEFT,
    plotWidth: HR_CURVE_PLOT_WIDTH,
    plotTop: HR_CURVE_PLOT_TOP,
    plotBottom: HR_CURVE_PLOT_BOTTOM,
  };
}

function shouldDimBaseCurve(
  canToggleRole,
  chartHighlightRole,
  hasSequenceSelection,
  showingRoleSegments = false
) {
  if (hasSequenceSelection) return true;
  if (showingRoleSegments) return true;
  return !!(chartHighlightRole && canToggleRole);
}

export default HrPerformanceCurveChart;
